// legacy, use Combobox / MultiCombobox instead
import { get, orderBy, debounce } from 'lodash';
import React from 'react';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/lib/Async';
import CreatableSelect from 'react-select/lib/Creatable';
import AsyncCreatableSelect from 'react-select/lib/AsyncCreatable';
import { Props } from 'react-select/lib/Select';
import { ValueType } from 'react-select/lib/types';
import { Close } from 'styled-icons/material/Close';
import { ChevronDown } from 'styled-icons/feather/ChevronDown';
import { getStyledSystemProps } from 'src/helpers';
import { useThemeContext, useMergeRefs } from 'src/hooks';
import { BaseBox, Box } from './Box';
import { Flex } from './Flex';
import { Text } from './Text';
import { StyledButton } from './StyledButton';
import { Label } from './Label';

const activeColor = '#07c5ea';
const textColor = '#4d535a';

const SelectDropdownIndicator = components.DropdownIndicator as React.ComponentClass<any, any>;

const DropdownIndicator = (props: any) => {
  if (props.selectProps.hideDropdownIndicator) return null;
  return (
    <SelectDropdownIndicator {...props}>
      <BaseBox as={ChevronDown} size={14} color="coolGrey" />
    </SelectDropdownIndicator>
  );
};

export const StyledSelect = React.forwardRef(
  (
    {
      value,
      formatValue,
      onChange,
      formatOnChange,
      styles = {},
      required,
      label,
      apiCall,
      isAsync,
      isCreatable,
      isMulti,
      useDefaultMultiValuesAppearance,
      showMultiValues = true,
      isDisabled,
      onRemove,
      multiValuesWrapper: MultiValuesWrapper = DefaultMultiValuesWrapper,
      components,
      children,
      ...rest
    }: any,
    outerRef: (node: HTMLElement | null) => void
  ) => {
    let SelectComponent: React.ComponentClass<any> = ReactSelect;
    if (isAsync) SelectComponent = AsyncSelect;
    if (isCreatable) SelectComponent = CreatableSelect;
    if (isAsync && isCreatable) SelectComponent = AsyncCreatableSelect;
    const theme = useThemeContext();
    const localRef = React.useRef<null | HTMLElement>(null);
    const mergedRef = useMergeRefs([outerRef, localRef]);
    React.useEffect(() => {
      if (localRef.current) {
        localRef.current.addEventListener('keydown', event => event.stopPropagation());
      }
    }, []);

    const [styledProps, restProps] = getStyledSystemProps(rest);
    const defaultRemoveHandler = item => onChange(value.filter((v: any) => v !== item));
    const onRemoveHandler = onRemove || defaultRemoveHandler;

    return (
      <Box
        ref={mergedRef}
        {...styledProps}
        data-testid={restProps['data-testid'] || 'react-select-data-testid-placeholder'}
      >
        <Label label={label} disabled={isDisabled} required={required} width={1}>
          <SelectComponent
            data-testid="123123123123132"
            components={{ DropdownIndicator, ...components }}
            cacheOptions
            styles={{ ...getCustomStyles(theme), ...styles }}
            isMulti={isMulti}
            controlShouldRenderValue={!isMulti || useDefaultMultiValuesAppearance}
            loadOptions={isAsync && loadOptions(apiCall, formatValue)}
            isDisabled={isDisabled}
            value={value}
            onChange={onChange}
            {...restProps}
          />
          {children}
        </Label>
        {isMulti &&
          !useDefaultMultiValuesAppearance &&
          showMultiValues &&
          Array.isArray(value) &&
          Boolean(value.length) && (
            <MultiValuesWrapper>
              {value.map((item: any, index) => (
                <MultiSelectItem key={item?.label || index} item={item} onRemove={onRemoveHandler}>
                  {item?.label || ''}
                </MultiSelectItem>
              ))}
            </MultiValuesWrapper>
          )}
      </Box>
    );
  }
);

export const DefaultMultiValuesWrapper = ({ children }) => (
  <Flex flexWrap="wrap" mt={14}>
    {children}
  </Flex>
);

export const MultiSelectItem = React.forwardRef(({ children, item, onRemove, ...styledProps }, ref) => (
  <Flex
    ref={ref}
    m="0 10px 10px 0"
    p="5px 18px"
    alignItems="center"
    maxWidth="100%"
    minHeight={36}
    borderRadius="20px"
    bg="blueGreyTwo"
    color="white"
    fontSize={14}
    {...styledProps}
  >
    <Text as="span" mr={10} textStyle="ellipsis">
      {children}
    </Text>
    <StyledButton title="remove" onClick={() => onRemove(item)} minWidth="14px" colors="whiteSmokeToWhite">
      <Close size={14} />
    </StyledButton>
  </Flex>
));

StyledSelect.defaultProps = {
  formatValue: v => v,
  formatOnChange: v => v,
  useDefaultMultiValuesAppearance: false,
  backspaceRemovesValue: false,
  isClearable: false,
  promptTextCreator: 'Add "{label}"?',
  addLabelText: 'Add "{label}"?',
  size: 'sm'
};

export const Select = ({ value, options, onChange, customShape, isSimple, isEntity, ...rest }: any) => {
  const controlProps = getFieldProps({
    customShape,
    isSimple,
    isEntity,
    value,
    options,
    onChange
  });

  return <StyledSelect {...controlProps} {...rest} />;
};

const formatValueToSelectValue = (value: ValueType<string | number>) =>
  value && {
    value,
    label: value
  };
const formatOptionsToSelectOptions = (options: Array<string | number>): any[] =>
  options && options.map(formatValueToSelectValue);

const formatSimpleValue = (value: any) =>
  Array.isArray(value) ? formatOptionsToSelectOptions(value) : formatValueToSelectValue(value);

const formatSelectItemByCustomKeys = ({ labelKey, valueKey, entity }) => ({
  label: get(entity, labelKey),
  value: get(entity, valueKey),
  data: entity
});
const formatSelectPropsByCustomKeys = ({ labelKey, valueKey, value }) => {
  if (!value) return value;
  return Array.isArray(value)
    ? value.map(item => formatSelectItemByCustomKeys({ labelKey, valueKey, entity: item }))
    : formatSelectItemByCustomKeys({ labelKey, valueKey, entity: value });
};

const getInitialValue = (v: any) => v && v.data;
export const getInitialValues = (v: OptionType | OptionType[]) =>
  Array.isArray(v) ? v.map(getInitialValue) : getInitialValue(v);

//Entity { id, name }
const formatSelectPropByEntity = (value: null | Entity | Entity[]) =>
  value && formatSelectPropsByCustomKeys({ labelKey: 'name', valueKey: 'id', value });

const loadOptions = (apiCall: (...params: any) => Promise<any>, formatValue) =>
  debounce((inputValue: string, callback: any) => {
    const q = inputValue.trim();
    apiCall({ params: { q } })
      .then(formatValue)
      .then(options =>
        orderBy(options, (option: any) => get(String(option.label).match(new RegExp(inputValue, 'i')), 'index'))
      )
      .then(result => callback(result));
  }, 250);

const getSimpleFieldProps = ({ value, options, onChange }) => ({
  formatValue: formatSimpleValue,
  value: formatSimpleValue(value),
  options: formatOptionsToSelectOptions(options),
  formatOnChange: parseSelectValue,
  onChange: (v: any) => onChange(parseSelectValue(v))
});

const getEntityFieldProps = ({ value, options, onChange }) => ({
  formatValue: formatSelectPropByEntity,
  value: formatSelectPropByEntity(value),
  options: formatSelectPropByEntity(options),
  formatOnChange: getInitialValues,
  onChange: (v: any) => onChange(getInitialValues(v))
});

const getCustomFieldProps = ({ labelKey, valueKey, value, options, onChange }) => {
  const formatValue = (value: any) => formatSelectPropsByCustomKeys({ value, labelKey, valueKey });
  return {
    formatValue,
    value: formatValue(value),
    options: formatSelectPropsByCustomKeys({ value: options, labelKey, valueKey }),
    formatOnChange: getInitialValues,
    onChange: (v: any) => onChange(getInitialValues(v))
  };
};

export const getFieldProps = ({ customShape, isSimple, isEntity, ...rest }: any) => {
  if (isSimple) return getSimpleFieldProps(rest);
  if (isEntity) return getEntityFieldProps(rest);
  if (customShape) return getCustomFieldProps({ ...customShape, ...rest });

  return rest;
};

type OptionType = { label: any; value: any };
type SelectProps<T> = Props<T> & { onChange: (value: ValueType<any>) => void };

const parseSelectValue = (data: ValueType<OptionType>) => {
  if (Array.isArray(data)) {
    return data.map(({ value }) => value);
  }
  return data && data.value;
};

export const SingleSelect = ({ onChange, ...props }: SelectProps<OptionType>) => {
  const theme = useThemeContext();
  return (
    <ReactSelect
      components={{ DropdownIndicator }}
      styles={getCustomStyles(theme)}
      onChange={data => onChange(parseSelectValue(data))}
      {...props}
    />
  );
};

const getCustomStyles = theme => ({
  clearIndicator: provided => ({
    ...provided,
    padding: '0 0 0 8px',
    width: '22px',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.coolGrey
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: state.selectProps.menuIsOpen ? '0 8px 0 0' : '0 0 0 8px',
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
    color: state.selectProps.isDisabled && get(theme.inputs[state.selectProps.variant], ['&:disabled', 'color'])
  }),
  container: provided => ({
    ...provided,
    height: theme.heights.legacySelect ? 34 : '100%'
  }),
  control: (provided, state) => {
    const variantStyles = theme.inputs[state.selectProps.variant] || {};
    const sizeStyles = theme.controlSizes[state.selectProps.size] || {};
    const controlStyles = {
      ...provided,
      boxShadow: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      ...variantStyles,
      ...sizeStyles,
      height: 'auto',
      minHeight: sizeStyles.height,
      ...(state.selectProps.isDisabled ? get(variantStyles, ['&:disabled'], {}) : {}),
      ...(state.isFocused ? get(variantStyles, ['&:focus'], {}) : {}),
      borderRadius: getBorderRadius(state, variantStyles, false)
    };
    if (state.selectProps.error) {
      controlStyles.backgroundColor = theme.colors.bgError;
      controlStyles.borderColor = theme.colors.error;
    }
    return controlStyles;
  },
  multiValue: provided => ({
    ...provided,
    backgroundColor: 'transparent',
    border: '1px solid #ccc'
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: '#ccc',
    ':hover': {
      color: '#000'
    }
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: theme.colors.coolGrey,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(state.selectProps.isDisabled
      ? get(theme.inputs[state.selectProps.variant], ['&:disabled', '&::placeholder'], {})
      : get(theme.inputs[state.selectProps.variant], '&::placeholder', {}))
  }),
  menu: (provided, state) => {
    const variantStyles = theme.inputs[state.selectProps.variant];
    return {
      ...provided,
      margin: 0,
      border: 'solid #e4e4e4',
      borderRadius: getBorderRadius(state, variantStyles, true),
      borderWidth: getBorderWidth(state, variantStyles),
      boxShadow: 'none',
      zIndex: 1000
    };
  },
  menuList: (provided, state) => {
    return {
      ...provided,
      padding: 0
    };
  },
  groupHeading: provided => ({
    ...provided,
    color: textColor,
    paddingLeft: 0,
    fontSize: '14px',
    fontWeight: 'bold',
    textTransform: 'initial'
  }),
  group: provided => ({
    ...provided,
    paddingLeft: '10px'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? activeColor : 'transparent',
    color: state.isSelected ? 'white' : textColor,
    cursor: 'pointer',
    fontSize: '14px',
    ':first-letter': {
      textTransform: 'capitalize'
    },
    ':hover': {
      backgroundColor: state.isSelected ? activeColor : theme.colors.paleGreyTwo
    }
  }),
  valueContainer: provided => ({
    ...provided,
    color: 'inherit',
    padding: 0
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  singleValue: provided => ({
    ...provided,
    color: 'inherit',
    ':first-letter': {
      textTransform: 'capitalize'
    }
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 9999
  }),
  input: provided => ({
    ...provided,
    height: theme.heights.legacySelect || '22px',
    paddingTop: theme.heights.legacySelect === 28 ? 5 : 2
  })
});

export const getBorderRadius = (state, currentStyles, isInvert) => {
  const currentBorderRadius = get(currentStyles, 'borderRadius', '2px');
  const { menuIsOpen, menuPlacement } = state.selectProps;
  if (menuIsOpen) {
    if ((menuPlacement === 'top' && !isInvert) || (menuPlacement === 'bottom' && isInvert)) {
      return `0 0 ${currentBorderRadius} ${currentBorderRadius}`;
    }
    return `${currentBorderRadius} ${currentBorderRadius} 0 0`;
  }
  return currentBorderRadius;
};

export const getBorderWidth = (state, currentStyles) => {
  const currentBorderWidth = get(currentStyles, 'borderWidth', 0);
  if (!currentBorderWidth) return 0;
  if (state.selectProps.menuPlacement === 'top') {
    return '1px 1px 0px';
  }
  return '0px 1px 1px';
};
